import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useQueryModal} from "@/hooks/useQueryModal.ts";
import {useTableQuery} from "@/hooks/useTableQuery.ts";
import {SortField, useNews} from "@/hooks/useNews.ts";
import {HeroHeader} from "@/components/HeroHeader.tsx";
import NewsGrid from "@/components/NewsGrid.tsx";
import {MetaTags} from "@/hooks/MetaTags.tsx";

const MIN_SEARCH_LEN = 3;
const DEBOUNCE_MS = 300;

export default function NewsPage() {
    const {searchParams, setParam} = useQueryModal();

    const {page, size, sortBy, sortOrder} = useTableQuery<SortField>({
        searchParams,
        setParam,
        defaultPage: 0,
        defaultSize: 12,
        defaultSortBy: "publishedAt",
        defaultSortOrder: "desc",
    });

    const qFromUrl = useMemo(() => searchParams.get("q") ?? "", [searchParams]);
    const [query, setQuery] = useState(qFromUrl);

    const lastAppliedQuery = useRef(qFromUrl);

    useEffect(() => setQuery(qFromUrl), [qFromUrl]);

    useEffect(() => {
        const id = setTimeout(() => {
            const v = query.trim();

            // Only update URL (and reset page) if the value differs from what’s already applied
            if (v.length >= MIN_SEARCH_LEN) {
                if (v !== lastAppliedQuery.current) {
                    setParam((p) => {
                        p.set("q", v);
                        p.set("page", "0");
                    });
                    lastAppliedQuery.current = v;
                }
            } else {
                // v too short → remove q only if it exists
                if (lastAppliedQuery.current) {
                    setParam((p) => {
                        p.delete("q");
                        p.set("page", "0");
                    });
                    lastAppliedQuery.current = "";
                }
            }
        }, DEBOUNCE_MS);

        return () => clearTimeout(id);
    }, [query, setParam]);
    const goToPage = useCallback(
        (next: number) => {
            setParam((p) => p.set("page", String(Math.max(0, next))));
        },
        [setParam]
    );

    const {data: result, isLoading, isError} = useNews(false, {
        page,
        size,
        sortBy,
        sortOrder,
        q: qFromUrl || undefined,
    });

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const v = query.trim();
        if (v.length >= MIN_SEARCH_LEN) {
            if (v !== lastAppliedQuery.current) {
                setParam((p) => {
                    p.set("q", v);
                    p.set("page", "0");
                });
                lastAppliedQuery.current = v;
            }
        } else if (lastAppliedQuery.current) {
            setParam((p) => {
                p.delete("q");
                p.set("page", "0");
            });
            lastAppliedQuery.current = "";
        }
    };

    const clearQuery = () => setQuery("");

    return (
        <>
            <MetaTags
                title={"Kosmos 94.1 - Jongste Nuus"}
                description={"Bly op hoogte met die jongste nuus van hier, daar en oral."}
                url={"/nuus"}
            />

            <HeroHeader
                title="Jongste Nuus"
                subtitle="Bly op hoogte met die jongste nuus van hier, daar en oral."
                imageAlt="nuus"/>

            <section className="py-16 sm:py-22">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <form onSubmit={onSubmit} className="relative mb-8">
                        <input
                            type="search"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Soek nuus…"
                            className="w-full rounded-2xl border border-gray-200 bg-white/70 px-4 py-3 pr-24 shadow-sm outline-none focus:ring-2 focus:ring-primary/40"
                            aria-label="Soek nuus"
                        />
                        {query && (
                            <button
                                type="button"
                                onClick={clearQuery}
                                className="absolute right-24 top-1/2 -translate-y-1/2 text-sm text-gray-500 hover:text-gray-700"
                            >
                            </button>
                        )}
                        <button
                            type="submit"
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xl px-3 py-2 text-sm font-medium bg-gray-800 text-white hover:bg-gray-800"
                        >
                            Soek
                        </button>
                    </form>

                    <NewsGrid
                        content={result}
                        isLoading={isLoading}
                        isError={isError}
                        seeMoreDisplay={false}
                        showPagination
                        setPage={goToPage}
                    />
                </div>
            </section>
        </>
    );
}
